import SubscriptionService from "@/services/subscription.service";

/**
 * Módulo de Vuex para manejar suscripciones, planes y módulos
 */
export const subscription = {
    namespaced: true,

    state: {
        // Suscripción efectiva del usuario
        subscription: null,
        hasSubscription: false,

        // Plan actual
        currentPlan: null,

        // Módulos activos
        activeModules: [],

        // Catálogo de planes disponibles
        availablePlans: [],

        // Estado de carga
        loading: false,
        error: null,
    },

    mutations: {
        SET_SUBSCRIPTION(state, data) {
            // Manejar diferentes estructuras de respuesta del backend
            let subscription = null;
            let hasSubscription = false;

            // Caso 1: Respuesta con estructura {has_subscription, subscription}
            if (data.has_subscription !== undefined) {
                hasSubscription = data.has_subscription;
                subscription = data.subscription;
            }
            // Caso 2: Respuesta con estructura { subscription: {...} } (endpoint actual)
            else if (data.subscription) {
                subscription = data.subscription;
                // El estado puede estar en subscription.status o inferirse de otros campos
                const status = subscription.status || (subscription.trial_ends_at ? 'trialing' : (subscription.ends_at ? 'active' : 'active'));
                subscription.status = status;
                hasSubscription = subscription && ['active', 'trialing', 'trial', 'past_due', 'cancelled'].includes(status);
            }
            // Caso 3: Respuesta directa con el objeto subscription
            else if (data.id && data.plan) {
                subscription = data;
                const status = subscription.status || (subscription.trial_ends_at ? 'trialing' : (subscription.ends_at ? 'active' : 'active'));
                subscription.status = status;
                hasSubscription = ['active', 'trialing', 'trial', 'past_due', 'cancelled'].includes(status);
            }
            // Caso 4: Sin suscripción
            else {
                hasSubscription = false;
                subscription = null;
            }

            state.subscription = subscription;
            state.hasSubscription = hasSubscription;
            state.currentPlan = subscription?.plan || null;
        },

        SET_ACTIVE_MODULES(state, modules) {
            state.activeModules = modules;
        },

        SET_AVAILABLE_PLANS(state, plans) {
            state.availablePlans = plans;
        },

        SET_LOADING(state, loading) {
            state.loading = loading;
        },

        SET_ERROR(state, error) {
            state.error = error;
        },

        CLEAR_SUBSCRIPTION(state) {
            state.subscription = null;
            state.hasSubscription = false;
            state.currentPlan = null;
            state.activeModules = [];
        },
    },

    actions: {
        /**
         * Obtiene la suscripción efectiva del usuario
         * Incluye plan completo con módulos
         */
        async fetchEffectiveSubscription({ commit }) {
            console.log('🔍 [Store Action] fetchEffectiveSubscription iniciado');
            commit("SET_LOADING", true);
            commit("SET_ERROR", null);

            try {
                const data = await SubscriptionService.getEffectiveSubscription();
                console.log('🔍 [Store Action] fetchEffectiveSubscription - datos del servicio:', data);
                commit("SET_SUBSCRIPTION", data);
                return data;
            } catch (error) {
                console.error('❌ [Store Action] Error en fetchEffectiveSubscription:', error);
                console.error('❌ [Store Action] error.response:', error.response);
                commit("SET_ERROR", error.message);
                throw error;
            } finally {
                commit("SET_LOADING", false);
            }
        },

        /**
         * Obtiene los módulos activos de la aplicación
         */
        async fetchActiveModules({ commit }, params = { per_page: 100 }) {
            commit("SET_LOADING", true);
            commit("SET_ERROR", null);

            try {
                const response = await SubscriptionService.getApplicationModules(params);
                commit("SET_ACTIVE_MODULES", response.data || []);
                return response;
            } catch (error) {
                commit("SET_ERROR", error.message);
                throw error;
            } finally {
                commit("SET_LOADING", false);
            }
        },

        /**
         * Obtiene el catálogo de planes disponibles
         */
        async fetchAvailablePlans({ commit }) {
            commit("SET_LOADING", true);
            commit("SET_ERROR", null);

            try {
                const response = await SubscriptionService.getPlans();
                // El backend puede retornar: response.data o response directamente
                const plans = Array.isArray(response) ? response : (response.data || []);
                commit("SET_AVAILABLE_PLANS", plans);
                return response;
            } catch (error) {
                commit("SET_ERROR", error.message);
                throw error;
            } finally {
                commit("SET_LOADING", false);
            }
        },

        /**
         * Inicia período de prueba
         */
        async startTrial({ commit, dispatch }, data) {
            commit("SET_LOADING", true);
            commit("SET_ERROR", null);

            try {
                const response = await SubscriptionService.startTrial(data);
                // Refrescar suscripción después de iniciar trial
                await dispatch("fetchEffectiveSubscription");
                return response;
            } catch (error) {
                commit("SET_ERROR", error.message);
                throw error;
            } finally {
                commit("SET_LOADING", false);
            }
        },

        /**
         * Suscribe al usuario a un plan
         */
        async subscribe({ commit, dispatch }, data) {
            commit("SET_LOADING", true);
            commit("SET_ERROR", null);

            try {
                const response = await SubscriptionService.subscribe(data);
                // Refrescar suscripción después de suscribirse
                await dispatch("fetchEffectiveSubscription");
                return response;
            } catch (error) {
                commit("SET_ERROR", error.message);
                throw error;
            } finally {
                commit("SET_LOADING", false);
            }
        },

        /**
         * Cambia el plan o ciclo de facturación
         */
        async changePlan({ commit, dispatch }, data) {
            commit("SET_LOADING", true);
            commit("SET_ERROR", null);

            try {
                const response = await SubscriptionService.changePlan(data);
                await dispatch("fetchEffectiveSubscription");
                return response;
            } catch (error) {
                commit("SET_ERROR", error.message);
                throw error;
            } finally {
                commit("SET_LOADING", false);
            }
        },

        /**
         * Actualiza el metodo de pago
         */
        async updatePaymentMethod({ commit, dispatch }, data) {
            commit("SET_LOADING", true);
            commit("SET_ERROR", null);

            try {
                const response = await SubscriptionService.updatePaymentMethod(data);
                await dispatch("fetchEffectiveSubscription");
                return response;
            } catch (error) {
                commit("SET_ERROR", error.message);
                throw error;
            } finally {
                commit("SET_LOADING", false);
            }
        },

        /**
         * Cancela la suscripción actual
         */
        async cancelSubscription({ commit, dispatch }, data = {}) {
            commit("SET_LOADING", true);
            commit("SET_ERROR", null);

            try {
                const response = await SubscriptionService.cancelSubscription(data);
                // Refrescar suscripción después de cancelar
                await dispatch("fetchEffectiveSubscription");
                return response;
            } catch (error) {
                commit("SET_ERROR", error.message);
                throw error;
            } finally {
                commit("SET_LOADING", false);
            }
        },

        /**
         * Reactiva una suscripcion cancelada (si no ha expirado)
         */
        async reactivateSubscription({ commit, dispatch }) {
            commit("SET_LOADING", true);
            commit("SET_ERROR", null);

            try {
                const response = await SubscriptionService.reactivateSubscription();
                await dispatch("fetchEffectiveSubscription");
                return response;
            } catch (error) {
                commit("SET_ERROR", error.message);
                throw error;
            } finally {
                commit("SET_LOADING", false);
            }
        },

        /**
         * Limpia los datos de suscripción (útil al hacer logout)
         */
        clearSubscription({ commit }) {
            commit("CLEAR_SUBSCRIPTION");
        },
    },

    getters: {
        // Información de suscripción
        hasActiveSubscription: (state) => {
            // Considera como activa si:
            // 1. El backend dice que tiene suscripción (has_subscription)
            // 2. O si el estado es 'trialing' o 'active'
            const hasSubscriptionFlag = state.hasSubscription;
            const status = state.subscription?.status;
            const isActive = hasSubscriptionFlag || ['trialing', 'active', 'past_due', 'cancelled'].includes(status);

            console.log('🔍 hasActiveSubscription getter:', {
                hasSubscription: hasSubscriptionFlag,
                status,
                isActive,
                subscription: state.subscription
            });

            return isActive;
        },
        subscription: (state) => state.subscription,
        subscriptionStatus: (state) => state.subscription?.status || null,
        subscriptionBillingCycle: (state) => state.subscription?.billing_cycle || 'monthly',
        daysRemaining: (state) => {
            if (state.subscription?.days_remaining !== undefined && state.subscription?.days_remaining !== null) {
                return state.subscription.days_remaining;
            }
            const trialEndsAt = state.subscription?.trial_ends_at;
            if (!trialEndsAt) return 0;
            const diffMs = new Date(trialEndsAt).getTime() - Date.now();
            return Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
        },
        isInTrial: (state) => state.subscription?.status === "trialing",

        // Información del plan
        currentPlan: (state) => state.currentPlan,
        planName: (state) => state.currentPlan?.name || "Sin plan",
        planCode: (state) => state.currentPlan?.code || null,
        planPrice: (state) => {
            const plan = state.currentPlan;
            const billingCycle = state.subscription?.billing_cycle;


            if (billingCycle === 'yearly' && plan?.price_yearly) {
                return plan.price_yearly;
            }
            return plan?.price_monthly || plan?.base_price || "0.00";
        },
        planCurrency: (state) => state.currentPlan?.currency || "MXN",
        planModules: (state) => {
            // Convertir objeto de módulos a array
            const modules = state.currentPlan?.modules;
            if (!modules) return [];

            // Si ya es un array, devolverlo
            if (Array.isArray(modules)) return modules;

            // Si es un objeto {EME: "...", POS: "..."}, convertirlo a array
            return Object.entries(modules).map(([code, name]) => ({
                code,
                name,
                pivot: { is_included: true }
            }));
        },

        // Módulos
        activeModules: (state) => state.activeModules,
        activeModuleCount: (state) => state.activeModules.length,

        // Verifica si un módulo específico está activo
        hasModule: (state) => (moduleCode) => {
            const modules = state.currentPlan?.modules;
            if (!modules) return false;

            // Si es objeto {EME: "...", POS: "..."}
            if (!Array.isArray(modules)) {
                return moduleCode in modules;
            }

            // Si es array
            return modules.some(
                (module) => module.code === moduleCode && module.pivot?.is_included
            );
        },

        // Obtiene módulos incluidos vs adicionales
        includedModules: (state) => {
            const modules = state.currentPlan?.modules;
            if (!modules) return [];

            // Si es objeto, convertir a array (todos incluidos)
            if (!Array.isArray(modules)) {
                return Object.entries(modules).map(([code, name]) => ({
                    code,
                    name,
                    pivot: { is_included: true }
                }));
            }

            // Si es array, filtrar incluidos
            return modules.filter((m) => m.pivot?.is_included);
        },

        additionalModules: (state) => {
            const modules = state.currentPlan?.modules;
            if (!modules) return [];

            // Si es objeto, no hay adicionales (todos son incluidos)
            if (!Array.isArray(modules)) {
                return [];
            }

            // Si es array, filtrar no incluidos
            return modules.filter((m) => !m.pivot?.is_included);
        },

        // Planes disponibles
        availablePlans: (state) => state.availablePlans,

        // Estados
        isLoading: (state) => state.loading,
        error: (state) => state.error,
    },
};
